import { useEffect, useRef, useCallback, type ReactNode } from "react";
import { Text, Box, Flex } from "@mantine/core";
import {
  InterviewQuestionInputType,
  Message,
  MessageRole,
  RequestStates,
} from "../../types";
import { MessageWrapper, MessageContent } from "../../components";
import { isVoiceTransportKey } from "../MessageContent";
import { translateIdentity, type StaticTextTranslator } from "../../utils";
import styles from "./MessageList.module.css";

/**
 * AS-1816: voice user-turns render as a standalone circular playback
 * button, so they shouldn't sit inside the regular rounded-rectangle
 * chat bubble. Detect them here so `MessageWrapper` can opt out of the
 * bubble shell.
 */
const isOutgoingVoiceMessage = (message: Message): boolean =>
  message.role === MessageRole.USER &&
  typeof message.content === "string" &&
  isVoiceTransportKey(message.content) &&
  (message.inputType === InterviewQuestionInputType.VOICE ||
    message.inputType == null);

interface MessageListProps {
  messages: Message[];
  /** Company name displayed on incoming messages */
  company?: string;
  /** Callback when the last message finishes loading */
  onLastMessageLoaded?: () => void;
  /** Choice buttons to display after the last message */
  choiceButtons?: ReactNode;
  /** Optional translation function for static chat labels */
  translate?: StaticTextTranslator;
  /** Active session id — needed to fetch presigned playback URLs for voice messages */
  sessionId?: string | null;
}

export function MessageList({
  messages,
  company = "Interviewer",
  onLastMessageLoaded,
  choiceButtons,
  translate = translateIdentity,
  sessionId = null,
}: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  if (messages.length === 0) {
    return (
      <Box className={styles.emptyState}>
        <Text c="dimmed" ta="center">
          {translate("Start the interview to begin the conversation")}
        </Text>
      </Box>
    );
  }

  return (
    <div className={styles.messageList} ref={containerRef}>
      <div className={styles.messageListInner}>
        {messages.map((message, index) => {
          const isIncoming = message.role === MessageRole.ASSISTANT;
          const isLastMessage = index === messages.length - 1;

          const bare = isOutgoingVoiceMessage(message);

          return (
            <MessageWrapper
              key={message.id}
              company={company}
              incoming={isIncoming}
              index={index}
              status={message.status || RequestStates.Success}
              onFinishedLoading={
                isLastMessage ? onLastMessageLoaded : undefined
              }
              bare={bare}
            >
              <MessageContent
                index={index}
                content={message.content}
                incoming={isIncoming}
                inputType={message.inputType}
                status={message.status}
                translate={translate}
                translateStaticContent={
                  message.metadata?.translateStaticContent === true
                }
                sessionId={sessionId}
              />
            </MessageWrapper>
          );
        })}

        {choiceButtons && (
          <Flex className={styles.choiceButtonsContainer} justify="flex-end">
            {choiceButtons}
          </Flex>
        )}

        <div ref={bottomRef} />
      </div>
    </div>
  );
}
